<template>
  <ExpandableCard :minHeightForMultiColumn="true" @show-more="onCardExpanded()" class="expandable-card bg-body-tertiary">
    <template #header>
      <span v-show="filtersApplied" class="filters-applied-note" >
        <i class="fa fa-filter"></i> filters applied
      </span>

      <!-- Slot is used for the header, generally an image with this provider's logo. -->
      <slot></slot>

    </template>

    <template #body>
      <LoadingSpinner v-if="waitingForResponse" class="text-secondary" style="margin-top: 10px;"></LoadingSpinner>

      <div v-else class="schedule">

        <div v-if="noFilmsToDisplay" class="no-films-found">
          <p>no films found in the<br>upcoming schedule</p>
        </div>

        <div v-for="(day) in displaySchedule.daysToDisplay(hideViewedFilms, filterByGenres)" :key="day.titleString()">
          <div class="schedule-date">{{day.titleString() + day.titleEmoji()}}</div>
          <div v-for="(program) in day.programsToDisplay(hideViewedFilms, filterByGenres)" :key="program.film_id" class="container">
            <span class="schedule-time">{{program.timeString()}}</span>
            <span class="film-link-block">
              <FilmLink
                  :film_name="program.filmName()"
                  :film_id="program.film_id"
                  :link_location="`Viewing Options Card: ${provider}`"
                  :show_if_bookmarked="true"
                  :show_flag_for_language_code="!['en','xx'].includes(program.film.original_language) ? program.film.original_language : null"
                  single_line="true"
              >
              </FilmLink>
            </span>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <span v-if="displaySchedule?.last_update" class="last-update-date">
        <i class="fa fa-calendar-check-o" aria-hidden="true"></i> {{displaySchedule.last_update}}
      </span>
    </template>
  </ExpandableCard>
</template>


<script>
import axios from "axios";
import {Film} from "@/film";
import FilmLink from "@/components/FilmLink.vue";
import {userStore} from "@/userStore";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import '@/date-utilities'
import ExpandableCard from "@/components/ExpandableCard.vue";
import analytics from "@/analytics";
import { getCurrentInstance } from 'vue';

// A scheduled program
class Program {
  constructor(_scheduledProgram) {
    this.start_time = new Date(_scheduledProgram.start_time);
    this.end_time = new Date(_scheduledProgram.end_time);
    this.film_id = _scheduledProgram.film_id;
    this.film = null;
  }

  isMarkedViewed() {
    return userStore.isFilmMarkedViewed(this.film_id);
  }

  matchesAnyGenreInList(_genreList) {
    const foundGenre = !!this.film?.genres?.find((programGenre) => {
      return _genreList.includes(programGenre);
    });

    return foundGenre;
  }

  getGenres() {
    if ( !this.film ) {
      return [];
    }
    return ;
  }

  aquireFilmData(_filmList) {
    if ( !this.film ) {
      this.film = _filmList.find((_film) => {
        return (this.film_id === _film._id);
      });
    }
  }

  timeString() {
    return this.start_time.toLocaleTimeString().replace(':00 AM', 'am').replace(':00 PM', 'pm');
  }

  filmName() {
    return this.film?.name ? this.film?.name : this.film_id;
  }

  static compareChronologically(_prog1, _prog2) {
    return (
        (_prog1.start_time < _prog2.start_time)? -1
            : (_prog1.start_time < _prog2.start_time)? 1
                : 0
    );
  }
}

// date formatter for displaying days
const dateFormatObj = new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'short', day: 'numeric' });

// A day of scheduled programs
class Day {
  constructor(_date) {
    this.dateObj = (_date instanceof Date)? new Date(_date) : null;
    this.programs = [];
  }

  matchesDate(_date) {
    return (_date instanceof Date)
        && (_date.getDate() === this.dateObj.getDate())
        && (_date.getMonth() === this.dateObj.getMonth())
        && (_date.getFullYear() === this.dateObj.getFullYear());
  }

  canAddProgram(_program) {
    return this.matchesDate(_program.start_time);
  }

  addProgram(_program) {
    if ( !this.canAddProgram((_program)) ) {
      throw Error("Trying to add a Program to a Day that can't be added to that day");
    }
    this.programs.push(_program);
    this.programs.sort(Program.compareChronologically)
  }

  populateWithFilmData(_filmList) {
    this.programs.forEach((_program) => {
      _program.aquireFilmData(_filmList);
    });
  }

  programsToDisplay(_hideViewedFilms, _filterByGenres) {
    return this.programs.filter((program) => {

      // hide viewed films if requested
      if ( _hideViewedFilms && program.isMarkedViewed() ) {
        return false;
      }

      // hide films that don't match any of the filtered genres
      if ( _filterByGenres && (_filterByGenres.length > 0) && !program.matchesAnyGenreInList(_filterByGenres) ) {
        return false;
      }

      // hide films that have already ended
      return (program.end_time > Date.now());
    });
  }


  titleEmoji() {

    const holidayFunctionsAndEmojis = {
      isNewYearsDay: '\u{1F389}',      // 🎉
      //isMlkDay: '\u{1F64F}',          // 🙏
      //isGroundhogDay: '\u{1F43E}',    // 🐾
      //isWashingtonsBirthday: '\u{1F1FA}\u{1F1F8}', // 🇺🇸
      //isPresidentsDay: '\u{1F3DB}\u{FE0F}',       // 🏛️
      isValentinesDay: '\u{2764}\u{FE0F}',   // ❤️
      //isLeapDay: '\u{1F438}',         // 🐸
      isStPatricksDay: '\u{1F340}',   // 🍀
      isEaster: '\u{1F430}', // 🐰
      isMothersDay: '\u{1F339}',      // 🌹
      isMemorialDay: '\u{1FA96}', // 🪖
      isFathersDay: '\u{1F454}',      // 👔
      //isJunteenth: '\u{1F389}',       // 🎉
      isIndependenceDay: '\u{1F9E8}', // 🧨
      //isLaborDay: '\u{1F4BC}',        // 💼
      //isColumbusDay: '\u{2693}',      // ⚓
      //isIndigenousPeoplesDay: '\u{1F30E}', // 🌎
      isHalloween: '\u{1F383}',       // 🎃
      //isVeteransDay: '\u{1F1FA}\u{1F1F8}', // 🇺🇸
      isThanksgiving: '\u{1F983}',    // 🦃
      //isBlackFriday: '\u{1F6CD}\u{FE0F}', // 🛍️
      isChristmas: '\u{1F384}',       // 🎄
      //isNewYearsEve: '\u{1F55B}'      // 🕛
    };

    for (const [funcName, emojiValue] of Object.entries(holidayFunctionsAndEmojis)) {
      if ( this.dateObj[funcName] && this.dateObj[funcName]() ) {
        // console.log(`${this.dateObj.toLocaleDateString()} ${funcName} ${emojiValue}`);
        return (emojiValue + ' ');
      }
    }
    return '';

  }

  titleString() {
    const kToday = new Date;
    const kTomorrow = new Date(kToday.getTime() + 24 * 60 * 60 * 1000);

    return this.matchesDate(kToday)? 'today'
        : this.matchesDate(kTomorrow) ? 'tomorrow'
            : dateFormatObj.format(this.dateObj)
  }

  static compareChronologically(_day1, _day2) {

    return (
        ( _day1.year !== _day2.year )? ( _day1.year - _day2.year )
            : ( _day1.month !== _day2.month ) ? ( _day1.month - _day2.month )
                : ( _day1.date !== _day2.date ) ? ( _day1.date - _day2.date )
                    : 0
    );
  }
}

// A collection of days of scheduled programs.
class Schedule {
  constructor() {
    this.days = [];
    this.last_update = null;
  }

  getYearRange() {
    const globalProps = getCurrentInstance().appContext.config.globalProperties;
    if ( globalProps.$screenshotMode &&
        Array.isArray(globalProps.$screenshotModeSettings?.viewingOptionsYearRange) &&
        globalProps.$screenshotModeSettings?.viewingOptionsYearRange.length === 2) {
      return globalProps.$screenshotModeSettings?.viewingOptionsYearRange;
    }
    return null;
  }

  async populate(_provider) {

    let requestUrl = `/api/viewing-options/scheduled/${_provider}`;
    let lastUpdateDisplay = null;

    // add range for screenshots, if one is set
    const range = this.getYearRange();
    if ( range )
      requestUrl += range.reduce((accQueryParams, curYear, curIndex) => {
        return accQueryParams + (curIndex===0? '?' : '&') + `range=${curYear}`
      }, '');

    // get the schedule for this provider
    return axios.get(requestUrl)
        .then((response) => {
          //console.log(requestUrl, response.data);
          let responseSchedule = response?.data?.schedule;
          if ( !responseSchedule ) {
            throw Error(`Invalid response from /api/viewing-options/${_provider}`);
          }
          responseSchedule.forEach((scheduledProgram) => {
            this.addProgram(new Program(scheduledProgram));
          });

          const last_update_date = new Date(response.data.last_update);
          lastUpdateDisplay = last_update_date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          });
          return axios.post('/api/film/viewing-options-films',{ film_ids: responseSchedule.map( program => program.film_id ) } );
        })
        .then((response) => {
          const responseFilmList = response.data.map(_film => new Film(_film));
          this.populateWithFilmData(responseFilmList);
          this.last_update = lastUpdateDisplay;
        });
  }

  addProgram(_program) {
    // find or add day
    let dayAdded = false;
    let dayIndex = this.days.findIndex((day) => {
      return day.canAddProgram(_program);
    });
    if ( dayIndex === -1 ) {
      this.days.push(new Day(_program.start_time));
      dayAdded = true;
      dayIndex = (this.days.length - 1);
    }

    // add the program and sort if a new day was added
    this.days[dayIndex].addProgram(_program);
    if ( dayAdded ) {
      this.days.sort(Day.compareChronologically);
    }
  }

  populateWithFilmData(_filmList) {
    this.days.forEach((_day) => {
      _day.populateWithFilmData(_filmList);
    })
  }

  daysToDisplay(_hideViewedFilms, _filterByGenres) {
    return this.days.filter((day) => {
      return (day.programsToDisplay(_hideViewedFilms, _filterByGenres).length > 0);
    });
  }
}

export default {
  name: "ScheduledFilmListCard",
  components: {ExpandableCard, LoadingSpinner, FilmLink},
  props: ['title', 'provider', 'hideViewedFilms', 'filterByGenres'],
  computed: {
    filtersApplied() {
      return ( this.hideViewedFilms || ( this.filterByGenres && this.filterByGenres.length > 0 ) );
    },
    noFilmsToDisplay() {
      return !(this.displaySchedule?.daysToDisplay(this.hideViewedFilms, this.filterByGenres)?.length > 0);
    }
  },
  data() {
    return {
      waitingForResponse: false,
      displaySchedule: new Schedule(),
      showExpanded: false
    }
  },

  async created() {

    this.waitingForResponse = true;
    this.displaySchedule.populate(this.provider)
      .catch((err) => {
        analytics.notifyBugsnagOfError(err);
      })
      .finally(() => {
        this.waitingForResponse = false;
      });
  },

  methods: {
    onCardExpanded() {
      analytics.sendEvent('viewing_option_expanded', {
        provider: this.provider
      });
    }
  }
}

</script>


<style scoped>

  .expandable-card {
    margin-bottom: 20px;

    .last-update-date {
      position: absolute;
      bottom: 3px;
      left: 5px;
      pointer-events: none;
      user-select: none;
      color: var(--bs-tertiary-color);
      opacity: 50%;
      font-size: 8px;
      margin-top: -4px;
    }

    .filters-applied-note {
      position: absolute;
      top: 47px;
      right: 1em;
      color: var(--bs-primary);
      opacity: 80%;
      font-size: 9px;
      margin-top: -4px;
    }

    .schedule {
      font-size: x-small;
    }

    .schedule-date {
      font-size: small;
      font-weight: 700;
      color: var(--bs-tertiary-color);
      padding-top: 5px;
      padding-bottom: 2px;
    }

    .schedule-time {
      display: inline-block;
      width: 50px;
    }

    .film-link-block {
      display: inline-block;

      /* minus 50px to make room for the schedule-time */
      width: -webkit-calc(100% - 50px);
      width: -moz-calc(100% - 50px);
      width: calc(100% - 50px);

    }

    .language-icon {
      padding-left: 0.2em;
      opacity: 80%;
    }
  }

  .no-films-found {
    margin-left: auto;
    margin-right: auto;
    width: 80%;
    text-align: center;
    font-size: small;
    font-weight: 700;
    color: var(--bs-tertiary-color);
    padding-top: 5px;
    padding-bottom: 2px;

    .membership-message {
      font-weight: normal;
      a {
        text-decoration: inherit;
        color: inherit;
        cursor: pointer;
        font-weight: 500;
        &:hover {
          color: var(--bs-link-hover-color);
        }
      }
    }
  }



</style>